import React from 'react';
import { Control, Controller, FieldErrors, UseFormWatch, UseFormRegister } from 'react-hook-form';
import { Form, Input, Switch, PhoneNumber } from '@crisellevaldez/speculo-ui';
import { Home } from 'lucide-react';
import { BrandingFormData } from '../../../../../schemas/branding.schema';
import { Organization } from '../../../../../types/organization.types';
import { formatPhoneNumber } from '../../../../../utils/formatters';
import { IconPill } from '../../../../common/IconPill';

interface PropertyDetailsSectionProps {
  control: Control<BrandingFormData>;
  register: UseFormRegister<BrandingFormData>;
  errors: FieldErrors<BrandingFormData>;
  watch: UseFormWatch<BrandingFormData>;
  selectedOrg?: Organization;
}

export const PropertyDetailsSection: React.FC<PropertyDetailsSectionProps> = ({
  control,
  register,
  errors,
  watch,
  selectedOrg,
}) => {
  const primaryContact = watch('primaryContact');

  return (
    <div className="bg-gray-50 dark:bg-white/5 rounded-lg p-6 border border-gray-300 dark:border-white/15">
      <div className="flex items-center gap-3 mb-3">
        <IconPill icon={Home} />
        <h4 className="text-sm font-bold text-foreground">Property Details Page</h4>
      </div>

      <hr className="dark:border-white/10" />

      <div className="mt-5">
        {/* Main content grid - Form fields on left, Preview on right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left side - Form fields */}
          <div className="space-y-6">
            {/* Primary Contact Toggle */}
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Primary Contact for Property Pages
                </h5>
                <Controller
                  name="primaryContact.useForPropertyPage"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value || false}
                      onChange={(checked) => field.onChange(checked)}
                      size="sm"
                    />
                  )}
                />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Override default contact in "Have Questions?" section
              </p>
            </div>

            {/* Brokerage Name - Only show when primary contact enabled */}
            {primaryContact?.useForPropertyPage && (
              <Form.Control>
                <Form.Label>Brokerage Name</Form.Label>
                <Input
                  {...register('brokerageName')}
                  placeholder="e.g., ABC Realty Group"
                />
              </Form.Control>
            )}

            {/* Contact Form Fields - Only show when enabled */}
            {primaryContact?.useForPropertyPage && (
              <div className="space-y-4">
                <Form.Control required>
                  <Form.Label>Contact Name</Form.Label>
                  <Input
                    {...register('primaryContact.name')}
                    placeholder="Contact name"
                    className={errors.primaryContact?.name ? 'border-red-500' : ''}
                  />
                  {errors.primaryContact?.name && (
                    <Form.HelperText className="text-red-500">
                      {errors.primaryContact.name.message}
                    </Form.HelperText>
                  )}
                </Form.Control>
                <div className="grid grid-cols-2 gap-4">
                  <Form.Control required>
                    <Form.Label>Email</Form.Label>
                    <Input
                      {...register('primaryContact.email')}
                      type="email"
                      placeholder="contact@example.com"
                      className={errors.primaryContact?.email ? 'border-red-500' : ''}
                    />
                    {errors.primaryContact?.email && (
                      <Form.HelperText className="text-red-500">
                        {errors.primaryContact.email.message}
                      </Form.HelperText>
                    )}
                  </Form.Control>
                  <Form.Control required>
                    <Form.Label>Phone</Form.Label>
                    <Controller
                      name="primaryContact.phone"
                      control={control}
                      render={({ field }) => (
                        <PhoneNumber
                          value={field.value || ''}
                          onChange={(value) => field.onChange(value || '')}
                          placeholder="Phone number"
                          defaultCountry="US"
                          className={errors.primaryContact?.phone ? 'border-red-500' : ''}
                        />
                      )}
                    />
                    {errors.primaryContact?.phone && (
                      <Form.HelperText className="text-red-500">
                        {errors.primaryContact.phone.message}
                      </Form.HelperText>
                    )}
                  </Form.Control>
                </div>
              </div>
            )}

            {/* Default info when disabled */}
            {!primaryContact?.useForPropertyPage && (
              <div className="p-4 bg-gray-100 dark:bg-white/10 rounded-lg border border-gray-200 dark:border-white/10">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Currently using default organization info:
                </p>
                <div className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    <span className="text-gray-500 dark:text-gray-400">Name:</span>{' '}
                    {selectedOrg?.org_name || 'Not set'}
                  </p>
                  <p>
                    <span className="text-gray-500 dark:text-gray-400">Phone:</span>{' '}
                    {selectedOrg?.phoneNumbers?.inbound
                      ? formatPhoneNumber(selectedOrg.phoneNumbers.inbound)
                      : 'Not set'}
                  </p>
                  <p>
                    <span className="text-gray-500 dark:text-gray-400">Email:</span>{' '}
                    {selectedOrg?.email || 'Not set'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right side - Preview Card (theme-aware approximation of the
              "Have Questions?" card on the visitor-facing property page) */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Preview {!primaryContact?.useForPropertyPage && '(Default)'}
            </p>
            <div className="border-2 border-gray-300 dark:border-white/15 rounded-lg shadow-lg overflow-hidden bg-white dark:bg-white/5">
              {/* Contact Card Header */}
              <div className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50 dark:from-white/5 dark:via-white/10 dark:to-white/5 px-4 py-3 border-b border-gray-200 dark:border-white/10">
                <h2 className="text-sm uppercase text-gray-800 dark:text-gray-200 font-semibold mb-0.5">
                  Have Questions?
                </h2>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Get in touch to schedule a tour or learn more
                </p>
              </div>

              <div className="p-4">
                {primaryContact?.useForPropertyPage ? (
                  <>
                    {/* Primary Contact Mode */}
                    <div className="mb-4">
                      {(watch('brokerageName') || selectedOrg?.brokerage_name) && (
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {watch('brokerageName') || selectedOrg?.brokerage_name}
                        </h3>
                      )}
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {primaryContact?.name || 'Contact Name'}
                      </p>
                    </div>
                    <div className="space-y-1 mb-4">
                      {primaryContact?.phone && (
                        <p className="text-sm text-gray-900 dark:text-gray-100 underline">
                          {formatPhoneNumber(primaryContact.phone)}
                        </p>
                      )}
                      {primaryContact?.email && (
                        <p className="text-sm text-gray-900 dark:text-gray-100 underline">
                          {primaryContact.email}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Default Mode - Shows website/team name */}
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {watch('name') || selectedOrg?.org_name || 'Organization Name'}
                      </h3>
                    </div>
                    <div className="space-y-1 mb-4">
                      {selectedOrg?.phoneNumbers?.inbound && (
                        <p className="text-sm text-gray-900 dark:text-gray-100 underline">
                          {formatPhoneNumber(selectedOrg.phoneNumbers.inbound)}
                        </p>
                      )}
                      {selectedOrg?.email && (
                        <p className="text-sm text-gray-900 dark:text-gray-100 underline">
                          {selectedOrg.email}
                        </p>
                      )}
                    </div>
                  </>
                )}
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="w-full text-white py-2.5 text-xs font-medium rounded-lg text-center"
                    style={{
                      backgroundColor: watch('colors.primary') || '#000000',
                    }}
                  >
                    Schedule a Tour
                  </button>
                  <button
                    type="button"
                    className="w-full bg-white py-2.5 text-xs font-medium rounded-lg border text-center"
                    style={{
                      color: watch('colors.primary') || '#000000',
                      borderColor: watch('colors.primary') || '#000000',
                    }}
                  >
                    Contact Agent
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scheduling Links - Bottom section */}
        <div className="border-t border-gray-200 dark:border-white/10 mt-6 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Control>
              <Form.Label>Tour Scheduling Link</Form.Label>
              <Input
                {...register('propertyDetails.tour')}
                placeholder="e.g., https://calendly.com/schedule-tour"
              />
              <Form.HelperText>Link for scheduling property tours</Form.HelperText>
            </Form.Control>

            <Form.Control>
              <Form.Label>Contact Info / Meeting Link</Form.Label>
              <Input
                {...register('propertyDetails.contact')}
                placeholder="e.g., https://calendly.com/your-link"
              />
              <Form.HelperText>Scheduling link for property inquiries</Form.HelperText>
            </Form.Control>
          </div>
        </div>
      </div>
    </div>
  );
};